import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import SubscriptionReceiverDataPreviewMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiver/SubscriptionReceiverDataPreview/subscriptionReceiverDataPreviewMUI";
import SubscriptionReceiverDuplicateCardMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverDuplicateCard/subscriptionReceiverDuplicateCardMUI";
import SubscriptionReceiverFormMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/subscriptionReceiverFormMUI";
import __ from "web/Layout/Translations";

import HtmlTemplate from "web/Components/Common/HtmlTemplate";

import getFormMode from "web/utils/page/product/subscription/forms/getFormMode";
import { getMultivalueFromOptions } from "web/utils/page/product/subscription/forms/prepareFormData";

import {
  FormView,
  HandleToggleReceiversModalFn,
  IDuplicateCardData,
  ISubscriptionFormData,
  ISubscriptionFormsFields,
  ISubscriptionReceiver,
  ReceiverStatus,
  SubscriptionCheckoutSteps,
  SubscriptionFormReceiver,
  SubscriptionStatus,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import { useGetEmployeeConfigurationQuery } from "web/features/employee/employeeApiSlice";
import { getEmployeeFieldsByGroupType } from "web/features/employee/utils";

interface ISubscriptionReceiverProps {
  data: ISubscriptionReceiver;
  index?: number;
  subscriptionId: string;
  wayChooseOthersInActiveForm?: SubscriptionFormReceiver;
  duplicateCardData?: Nullable<IDuplicateCardData>;
  viewType: FormView;
  configuratorStep?: SubscriptionCheckoutSteps;
  benefitStatus: string;
  enableOrderDuplicateCard: boolean;
  duplicateTrigger: boolean;
  openDuplicateForm: Nullable<string>;
  setOpenDuplicateForm: React.Dispatch<React.SetStateAction<Nullable<string>>>;
  updateReceiversDataFunc?: (newReceiverData: ISubscriptionReceiver) => void;
  handleToggleReceiversModal?: HandleToggleReceiversModalFn;
  name: string;
  groupName: string;
  benefitGroupType?: number | undefined;
}

export const SubscriptionReceiverMessages = {
  employeeEditDisabled:
    "Automatycznie zapisaliśmy Twoje dane, ponieważ ten abonament możesz wybrać tylko dla siebie.",
  formSaveSuccess: "Nowe dane zostały zapisane.",
  formSaveError: "Nowe dane nie zostały zapisane.",
  editEmailSuccess: "Na podany e-mail został wysłany formularz.",
  editEmailError: "Nie udało się wysłać maila, spróbuj ponownie później.",
};

const SubscriptionReceiverMUI: FC<ISubscriptionReceiverProps> = ({
  data,
  index = 0,
  subscriptionId,
  wayChooseOthersInActiveForm = SubscriptionFormReceiver.EMPLOYEE,
  duplicateCardData,
  viewType,
  configuratorStep = SubscriptionCheckoutSteps.INFORMATIONS,
  benefitStatus,
  enableOrderDuplicateCard,
  openDuplicateForm,
  duplicateTrigger,
  handleToggleReceiversModal,
  setOpenDuplicateForm,
  updateReceiversDataFunc,
  name,
  groupName,
  benefitGroupType,
}) => {
  const { benefitGroupType: benefitGroupTypeQuote } = useSelector(
    (state) => state.subscriptionQuote
  );
  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();
  const {
    subscriptionAllowChangingPersonalData,
    formEditInfoBlockEmployee,
    formEditInfoBlockOthers,
  } = getEmployeeFieldsByGroupType(
    [
      "subscriptionAllowChangingPersonalData",
      "formEditInfoBlockEmployee",
      "formEditInfoBlockOthers",
    ],
    // Use value from subscriptionQuote on configurator (which is taken from URL)
    // and use value from benefitGroup.groupType response on my subscriptions
    viewType === FormView.MY_SUBSCRIPTIONS && benefitGroupType
      ? String(benefitGroupType)
      : benefitGroupTypeQuote,
    employeeConfig
  ) as { [key: string]: Nullable<string> };

  const [formData, updateFormData] = useState<ISubscriptionFormData>({
    edited: false,
    fields: {},
    filledInByUser: false,
    error: false,
  });
  const fieldsRef = useRef({});
  const {
    form,
    id: receiverId,
    type: receiverType,
    status: receiverStatus,
  } = data;

  if (typeof form !== "object") return null;

  const { fields, receiverEmail, type: formType } = form;
  const mode = getFormMode({
    form,
    formData,
    receiverType,
    subscriptionAllowChangingPersonalData,
    wayChooseOthersInActiveForm,
    configuratorStep,
  });
  const editInfoBlock =
    receiverType === SubscriptionFormReceiver.EMPLOYEE
      ? formEditInfoBlockEmployee
      : formEditInfoBlockOthers;

  useEffect(() => {
    const formFields: ISubscriptionFormsFields = {};

    fields.forEach(({ id, options, value, type }) => {
      formFields[id] = value || getMultivalueFromOptions(options || [], type);
    });

    if (mode.externalForm) {
      formFields.receiverEmail = receiverEmail ?? "";
    }

    // save preview data not changed in Formik - i.e. firstname, lastname for external forms
    fieldsRef.current = formFields;

    updateFormData({ ...formData, fields: formFields });
  }, [data]);

  /**
   * Benefit musi być aktywny (zrezygnowane nie wyświetlaja się na konfiguratorze)
   * receiverStatus - Użytkownik musi mieć status aktywny lub zrezygnowany
   * enableOrderDuplicateCard - czy jest włączona możliwość zamawiania duplikatu
   * duplicateCardData - czy dane dot. duplikatów są obecne
   */
  const shouldShowDuplicateButton =
    (benefitStatus === SubscriptionStatus.ACTIVE ||
      benefitStatus === SubscriptionStatus.RESIGNED) &&
    (receiverStatus === ReceiverStatus.ACTIVE ||
      receiverStatus === ReceiverStatus.RESIGNED) &&
    enableOrderDuplicateCard &&
    duplicateCardData &&
    duplicateTrigger;

  return (
    <Box>
      {editInfoBlock && formData.edited && (
        <Box pb={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minWidth: "283px",
              bgcolor: "#EDF7ED",
              py: { xs: 1, md: 2 },
              px: 2,
              mt: 2,
              mb: 1,
            }}
          >
            <CheckCircleOutlineIcon
              color="success"
              sx={{
                width: "16px",
                mr: 1.5,
                position: "relative",
                top: "-5px",
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: "#1E4620", width: "100%" }}
            >
              {__(
                mode.externalForm
                  ? SubscriptionReceiverMessages.editEmailSuccess
                  : SubscriptionReceiverMessages.formSaveSuccess
              )}
            </Typography>
          </Box>
          <HtmlTemplate template={editInfoBlock} />
        </Box>
      )}
      <Typography variant="subtitle2" component="div" mb={1}>
        {__("Osoba")} {index + 1}
      </Typography>
      {mode.preview && (
        <SubscriptionReceiverDataPreviewMUI
          fields={fields}
          formData={formData}
          formType={formType}
          mode={mode}
          viewType={viewType}
        />
      )}
      {(!duplicateTrigger || viewType === FormView.CONFIGURATOR) &&
        formData.fields &&
        Object.keys(formData.fields).length > 0 && (
          <SubscriptionReceiverFormMUI
            data={form}
            formData={formData}
            mode={mode}
            receiverId={receiverId}
            receiverType={receiverType}
            subscriptionId={subscriptionId}
            viewType={viewType}
            updateFormData={updateFormData}
            handleToggleReceiversModal={handleToggleReceiversModal}
            fieldsRef={fieldsRef}
            subscriptionName={name}
            groupName={groupName}
          />
        )}
      {shouldShowDuplicateButton && (
        <SubscriptionReceiverDuplicateCardMUI
          data={data}
          duplicateCardData={duplicateCardData}
          enableOrderDuplicateCard={enableOrderDuplicateCard}
          openDuplicateForm={openDuplicateForm}
          setOpenDuplicateForm={setOpenDuplicateForm}
          updateReceiversDataFunc={updateReceiversDataFunc}
          viewType={viewType}
        />
      )}
    </Box>
  );
};

export default SubscriptionReceiverMUI;
